import { ArrowUpRight, Disc3 } from 'lucide-react'
import { useMemo, useState } from 'react'

/* The analytics dashboard your clients' artists actually see.
 *
 * Mirrored from the SHIPPED dashboard (frontend/pages/portal/workspace/
 * Analytics.tsx, which enterprise/workspace/Analytics re-exports verbatim so
 * an enterprise tenant's artists get the identical screen). Everything here
 * exists there: the Revenue / Your Share / Total Units stat row with a delta
 * badge, the range picker, the five-card insight row, the per-platform revenue
 * trend with click-to-toggle series, the platform breakdown table carrying RPM
 * and payout RPM, and a ranked list showing units, revenue and share on one
 * row with a share bar under it.
 *
 * An earlier version of this mock was a thin two-panel sketch, and it sold the
 * product short — the real screen is considerably better than the drawing of
 * it was. That is the failure mode worth guarding against here: a mock that
 * UNDERSELLS is as much a problem as one that oversells, because this is the
 * screen a label judges a distributor on when it is deciding whether to leave
 * one.
 *
 * RPM is kept in, deliberately, even though it needs a footnote. It is the
 * number that separates a real royalty dashboard from a streaming-counts
 * widget, and a buyer who knows what RPM is will look for it.
 *
 * ALL DATA IS DETERMINISTIC AND FABRICATED, and the totals are DERIVED from
 * the series rather than typed in. Deterministic because these pages are
 * server-rendered then hydrated, and a Math.random() series renders one shape
 * on the server and another in the browser. Derived because a dashboard whose
 * columns do not add up is the fastest way to lose a numerate reader.
 */

type Range = 12 | 6 | 3

interface Platform {
  name: string
  color: string
  /** Twelve months of revenue, oldest first, in whole units of account currency. */
  revenue: number[]
  /** Streaming units for the same window, used for RPM. */
  units: number[]
}

/* Only platforms wavbee genuinely delivers to — config/stores.json is the
   source of truth and these pages carry the same undelivered-platform guard as
   the rest of the marketing site. */
const PLATFORMS: Platform[] = [
  {
    name: 'Spotify',
    color: '#1db954',
    revenue: [4120, 4480, 4310, 5020, 5340, 5210, 6080, 6440, 6910, 7220, 7880, 8410],
    units: [1020000, 1105000, 1064000, 1240000, 1318000, 1286000, 1501000, 1590000, 1706000, 1783000, 1946000, 2077000],
  },
  {
    name: 'Apple Music',
    color: '#fa586a',
    revenue: [2380, 2510, 2620, 2740, 2690, 2980, 3110, 3340, 3520, 3610, 3880, 4120],
    units: [312000, 329000, 344000, 359000, 353000, 391000, 408000, 438000, 462000, 474000, 509000, 541000],
  },
  {
    name: 'YouTube Music',
    color: '#ff4e45',
    revenue: [1180, 1240, 1390, 1330, 1470, 1610, 1580, 1740, 1890, 1960, 2110, 2280],
    units: [788000, 828000, 928000, 888000, 981000, 1075000, 1055000, 1162000, 1262000, 1309000, 1409000, 1522000],
  },
  {
    name: 'JioSaavn',
    color: '#2bc5b4',
    revenue: [640, 710, 780, 830, 910, 970, 1060, 1120, 1210, 1290, 1380, 1470],
    units: [901000, 999000, 1098000, 1168000, 1281000, 1365000, 1492000, 1577000, 1703000, 1816000, 1943000, 2069000],
  },
  {
    name: 'Amazon',
    color: '#ff9900',
    revenue: [520, 560, 590, 610, 660, 690, 720, 780, 810, 860, 900, 960],
    units: [118000, 127000, 134000, 138000, 150000, 156000, 163000, 177000, 184000, 195000, 204000, 218000],
  },
]

const MONTH_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']

const TOP_TRACKS = [
  { track: 'Slow Monsoon', artist: 'Ana Reyes', units: 2_140_000, revenue: 8_420 },
  { track: 'Paper Radio', artist: 'The Long Field', units: 1_688_000, revenue: 6_310 },
  { track: 'Held Note', artist: 'Osei', units: 1_204_000, revenue: 4_870 },
  { track: 'Coastal Static', artist: 'Ana Reyes', units: 908_000, revenue: 3_240 },
]

const ROYALTY_PCT = 0.9

/* ── Formatting, matching the shipped dashboard's helpers ────────────────── */
const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

function compact(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return String(n)
}

/* ── Chart geometry ──────────────────────────────────────────────────────── */
const CW = 640
const CH = 190
const PAD_B = 22

export default function AnalyticsDashboardMockup() {
  const [range, setRange] = useState<Range>(12)
  const [hidden, setHidden] = useState<string[]>([])

  // Memoized on `hidden`, not rebuilt in the render body. As a bare
  // `PLATFORMS.filter(...)` this was a fresh array identity every render, so it
  // was a dependency that always changed and the memo below could never hit —
  // it recomputed unconditionally while looking like it was cached.
  const visible = useMemo(
    () => PLATFORMS.filter((p) => !hidden.includes(p.name)),
    [hidden],
  )

  const view = useMemo(() => {
    const slice = <T,>(arr: T[]) => arr.slice(-range)
    const months = slice(MONTH_LABELS)

    const totalRevenue = visible.reduce((sum, p) => sum + slice(p.revenue).reduce((a, b) => a + b, 0), 0)
    const totalUnits = visible.reduce((sum, p) => sum + slice(p.units).reduce((a, b) => a + b, 0), 0)

    // Month-on-month delta on the last two points, which is what the real
    // DeltaBadge shows.
    const monthTotal = (offset: number) =>
      visible.reduce((sum, p) => sum + p.revenue[p.revenue.length - offset], 0)
    const delta = visible.length
      ? ((monthTotal(1) - monthTotal(2)) / Math.max(monthTotal(2), 1)) * 100
      : 0

    const rows = visible
      .map((p) => {
        const revenue = slice(p.revenue).reduce((a, b) => a + b, 0)
        const units = slice(p.units).reduce((a, b) => a + b, 0)
        return { name: p.name, color: p.color, revenue, units, rpm: (revenue / units) * 1000 }
      })
      .sort((a, b) => b.revenue - a.revenue)

    const max = Math.max(...visible.flatMap((p) => slice(p.revenue)), 1)

    // Derived from figures this memo already holds, so they belong inside it.
    // As render-body statements the RPM pair copied and re-sorted `rows` on
    // every render for a value that only changes when `rows` does.
    const blendedRpm = (totalRevenue / Math.max(totalUnits, 1)) * 1000
    const bestRpm = rows.length ? [...rows].sort((a, b) => b.rpm - a.rpm)[0] : null

    return { months, totalRevenue, totalUnits, delta, rows, max, blendedRpm, bestRpm }
  }, [range, visible])

  function toggle(name: string) {
    setHidden((current) =>
      current.includes(name)
        ? current.filter((n) => n !== name)
        : // Never let the reader empty the chart — an all-off state is a bug
          // report, not an insight.
          current.length === PLATFORMS.length - 1
          ? current
          : [...current, name]
    )
  }

  const line = (values: number[]) => {
    const pts = values.slice(-range)
    const step = CW / Math.max(pts.length - 1, 1)
    return pts
      .map((v, i) => {
        const x = i * step
        const y = (CH - PAD_B) - (v / view.max) * (CH - PAD_B - 8)
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
      })
      .join(' ')
  }

  const { blendedRpm, bestRpm } = view

  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 sm:p-8">
      <div className="mb-6 text-center">
        <p className="font-mono text-[0.6875rem] uppercase tracking-[0.16em] text-[#ff8c4d]">
          Royalty analytics
        </p>
        <h3
          className="mx-auto mt-2 max-w-2xl text-lg font-semibold leading-snug text-white sm:text-xl"
          style={{ fontFamily: 'var(--wavbee-font-display)' }}
        >
          Where the money came from, down to the statement line.
        </h3>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0b0b0c]">
        {/* ── Stat row + range picker ───────────────────────────────────── */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/[0.07] px-5 py-4">
          <div className="flex flex-wrap items-end gap-6 sm:gap-9">
            <Stat label="Revenue" value={money(view.totalRevenue)} sub={`${range} months`} accent />
            <Stat label="Your share" value={money(view.totalRevenue * ROYALTY_PCT)} sub="90% royalty" />
            <Stat label="Total units" value={compact(view.totalUnits)} sub="streams + downloads" />
            <span className="mb-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6875rem] font-medium text-emerald-400">
              <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              {view.delta.toFixed(1)}%
            </span>
          </div>
          <div className="flex gap-1" role="group" aria-label="Date range">
            {([12, 6, 3] as Range[]).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={range === r}
                onClick={() => setRange(r)}
                className={`rounded-md px-2.5 py-1 font-mono text-[0.6875rem] transition-colors ${
                  range === r
                    ? 'bg-[#ff6a1a]/12 text-[#ff8c4d]'
                    : 'text-white/35 hover:text-white/70'
                }`}
              >
                {r}M
              </button>
            ))}
          </div>
        </div>

        {/* ── Insight row ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-2.5 border-b border-white/[0.07] p-4 lg:grid-cols-4">
          <Insight label="Top platform" value={view.rows[0]?.name ?? '—'} detail={`${money(view.rows[0]?.revenue ?? 0)} this window`} />
          <Insight label="Best RPM" value={bestRpm ? `$${bestRpm.rpm.toFixed(2)}` : '—'} detail={bestRpm?.name ?? ''} />
          <Insight label="Blended RPM" value={`$${blendedRpm.toFixed(2)}`} detail="across every platform" />
          <Insight label="Top market" value="India" detail="38% of units" />
        </div>

        {/* ── Revenue by platform ───────────────────────────────────────── */}
        <div className="border-b border-white/[0.07] p-5">
          <h4 className="text-[0.8125rem] font-semibold text-white">Revenue by platform</h4>
          <p className="mt-0.5 text-[0.6875rem] text-white/35">
            Per-platform revenue by transaction month. Click a platform to toggle it.
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Toggle platforms">
            {PLATFORMS.map((p) => {
              const on = !hidden.includes(p.name)
              return (
                <button
                  key={p.name}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(p.name)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.6875rem] transition-all ${
                    on
                      ? 'border-white/20 text-white/80'
                      : 'border-white/[0.07] text-white/25 hover:text-white/50'
                  }`}
                >
                  <span
                    className="h-2 w-2 rounded-full transition-opacity"
                    style={{ background: p.color, opacity: on ? 1 : 0.3 }}
                    aria-hidden="true"
                  />
                  {p.name}
                </button>
              )
            })}
          </div>

          <svg
            viewBox={`0 0 ${CW} ${CH}`}
            className="mt-4 h-auto w-full"
            role="img"
            aria-label={`Revenue trend across ${visible.length} platforms over the last ${range} months`}
          >
            {[0.25, 0.5, 0.75].map((f) => (
              <line
                key={f}
                x1="0"
                x2={CW}
                y1={(CH - PAD_B) * f}
                y2={(CH - PAD_B) * f}
                stroke="rgba(255,255,255,0.05)"
              />
            ))}
            {visible.map((p) => (
              <path
                key={p.name}
                d={line(p.revenue)}
                fill="none"
                stroke={p.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity="0.9"
              />
            ))}
            {view.months.map((m, i) => (
              <text
                key={`${m}-${i}`}
                x={(CW / Math.max(view.months.length - 1, 1)) * i}
                y={CH - 4}
                textAnchor={i === 0 ? 'start' : i === view.months.length - 1 ? 'end' : 'middle'}
                fill="rgba(255,255,255,0.22)"
                style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}
              >
                {m}
              </text>
            ))}
          </svg>
        </div>

        {/* ── Platform breakdown ────────────────────────────────────────── */}
        <div className="border-b border-white/[0.07] p-5">
          <h4 className="text-[0.8125rem] font-semibold text-white">Platform breakdown</h4>
          <p className="mt-0.5 text-[0.6875rem] text-white/35">
            RPM is revenue per 1,000 streaming units — the number that says which platform is
            actually worth chasing.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[30rem] text-left">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  {['Platform', 'Units', 'Revenue', 'Share', 'RPM'].map((h, i) => (
                    <th
                      key={h}
                      className={`pb-2 font-mono text-[10px] uppercase tracking-[0.1em] text-white/30 ${
                        i === 0 ? 'text-left' : 'text-right'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row) => (
                  <tr key={row.name} className="border-b border-white/[0.04] last:border-0">
                    <td className="py-2 text-[0.75rem] text-white/80">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ background: row.color }}
                          aria-hidden="true"
                        />
                        {row.name}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono text-[0.75rem] tabular-nums text-white/45">
                      {compact(row.units)}
                    </td>
                    <td className="py-2 text-right font-mono text-[0.75rem] tabular-nums text-white/85">
                      {money(row.revenue)}
                    </td>
                    <td className="py-2 text-right font-mono text-[0.75rem] tabular-nums text-white/45">
                      {((row.revenue / Math.max(view.totalRevenue, 1)) * 100).toFixed(1)}%
                    </td>
                    <td className="py-2 text-right font-mono text-[0.75rem] tabular-nums text-[#ff8c4d]">
                      ${row.rpm.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Ranked list ───────────────────────────────────────────────── */}
        <div className="p-5">
          <div className="mb-3 flex items-center gap-2">
            <Disc3 className="h-3.5 w-3.5 text-white/40" aria-hidden="true" />
            <h4 className="text-[0.8125rem] font-semibold text-white">Top tracks</h4>
          </div>
          <div className="space-y-2.5">
            {TOP_TRACKS.map((t) => (
              <div key={t.track}>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[0.75rem] font-medium text-white">{t.track}</p>
                    <p className="truncate text-[0.6875rem] text-white/30">{t.artist}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 font-mono tabular-nums">
                    <span className="text-[0.6875rem] text-white/30">{compact(t.units)} units</span>
                    <span className="text-[0.75rem] font-medium text-white">{money(t.revenue)}</span>
                    <span className="w-10 text-right text-[0.6875rem] text-white/40">
                      {((t.revenue / TOP_TRACKS[0].revenue) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-white/[0.05]">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#ff6a1a]/60 to-[#ff6a1a]/20"
                    style={{ width: `${(t.revenue / TOP_TRACKS[0].revenue) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-5 text-center text-[0.75rem] leading-relaxed text-white/45">
        The console view — filters, per-platform toggles, RPM, territory and release breakdowns,
        and a drill-down to the statement line behind any figure. Artists get a simpler version of
        the same data in their own portal. Sample data.
      </p>
    </div>
  )
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/35">{label}</p>
      <p
        className={`mt-0.5 text-lg font-bold tabular-nums sm:text-xl ${accent ? 'text-[#ff6a1a]' : 'text-white'}`}
        style={{ fontFamily: 'var(--wavbee-font-display)' }}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[0.625rem] text-white/35">{sub}</p>}
    </div>
  )
}

function Insight({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-white/[0.07] bg-white/[0.02] px-3.5 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/30">{label}</p>
      <p
        className="mt-1 truncate text-[0.875rem] font-semibold text-white"
        style={{ fontFamily: 'var(--wavbee-font-display)' }}
      >
        {value}
      </p>
      <p className="mt-0.5 truncate text-[0.625rem] text-white/35">{detail}</p>
    </div>
  )
}
